import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';

import { GetCitiesDataTrash, UpdateOneCityData } from '../../Redux/Actions/CityAction';
import { Notify }          from '../../Utility/Notify';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Accent ──────────────────────────────────────────────────────────────── */
const ACCENT      = '#0D47A1';
const ACCENT_DARK = '#0a3880';
const ACCENT_SOFT = 'rgba(13,71,161,0.09)';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconCopy     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconRestore  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>;
const IconClose    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconWarning  = () => <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IconCity     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V21H3V9.5z"/><rect x="9" y="14" width="6" height="7" rx="1"/></svg>;
const IconCalendar = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconTrend    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;

/* ─── Portal Confirm Modal ────────────────────────────────────────────────── */
const PortalModal = ({ isDark, title, subtitle, onClose, onConfirm, confirmLabel, confirmColor, loading }) =>
  createPortal(
    <div
      style={{ position:'fixed',inset:0,zIndex:999999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.75)',backdropFilter:'blur(10px)',animation:'pm-fade 0.2s ease both' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width:'100%',maxWidth:'480px',margin:'0 20px',borderRadius:'24px',padding:'36px 32px 32px',background:isDark?'linear-gradient(135deg,#1a1f2e,#141829)':'#fff',boxShadow:'0 40px 100px rgba(0,0,0,0.35)',position:'relative',animation:'pm-slide 0.26s cubic-bezier(0.34,1.2,0.64,1) both',color:isDark?'#E4E8F5':'#1C2340' }}>
        <button onClick={onClose} style={{ position:'absolute',top:16,right:16,width:34,height:34,borderRadius:'10px',border:`1px solid ${isDark?'#3A4570':'#E8EAF0'}`,background:isDark?'#252D4A':'#F7F8FC',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:isDark?'#9099c0':'#7B829A' }}>
          <IconClose />
        </button>
        <div style={{ width:76,height:76,borderRadius:'50%',background:`linear-gradient(135deg,${confirmColor}22,${confirmColor}44)`,border:`2px solid ${confirmColor}66`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px',color:confirmColor }}>
          <IconWarning />
        </div>
        <p style={{ fontSize:'20px',fontWeight:700,textAlign:'center',margin:'0 0 8px',letterSpacing:'-0.3px',fontFamily:"'DM Sans',sans-serif" }}>{title}</p>
        <p style={{ fontSize:'13px',color:isDark?'#7B829A':'#6b7280',textAlign:'center',margin:'0 0 28px',lineHeight:1.6,fontFamily:"'DM Sans',sans-serif" }}>{subtitle}</p>
        <div style={{ display:'flex',gap:'10px' }}>
          <button onClick={onClose} disabled={loading} style={{ flex:1,padding:'13px',borderRadius:'12px',border:`1.5px solid ${isDark?'#3A4570':'#E8EAF0'}`,background:isDark?'#252D4A':'#F7F8FC',color:isDark?'#E4E8F5':'#1C2340',fontFamily:"'DM Sans',sans-serif",fontSize:'14px',fontWeight:600,cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ flex:1,padding:'13px',borderRadius:'12px',border:'none',background:`linear-gradient(135deg,${confirmColor},${confirmColor}cc)`,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'14px',fontWeight:700,cursor:loading?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',opacity:loading?0.7:1 }}>
            {loading
              ? <><div style={{ width:16,height:16,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'pm-spin 0.7s linear infinite' }}/>Processing…</>
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const CitiesTrashPage = () => {
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [loading,        setLoading]        = useState(true);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [page,           setPage]           = useState(1);
  const [itemsPerPage,   setItemsPerPage]   = useState(10);
  const [selectedItems,  setSelectedItems]  = useState([]);
  const [copyOpen,       setCopyOpen]       = useState(false);
  const [confirmModal,   setConfirmModal]   = useState({ open:false, id:null, name:'' });
  const [userlogin,      setUserlogin]      = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const getallcities = async () => {
    setLoading(true);
    await dispatch(GetCitiesDataTrash());
    setLoading(false);
  };
  useEffect(() => { getallcities(); }, []);

  const resAllCities = useSelector(s => s.AllCities.city);
  const allItems     = Array.isArray(resAllCities?.data) ? resAllCities.data : [];

  /* filter */
  const filtered = allItems.filter(item => {
    const q = searchTerm.toLowerCase();
    return !q || item.name?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems  = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /* stats */
  const thisMonth = allItems.filter(i => moment(i.createdAt).isAfter(moment().subtract(30,'days'))).length;

  /* home route */
  const homeRoute = userlogin?.data?.role === 'Admin'
    ? '/AllLeadsForAdmin'
    : userlogin?.data?.role === 'Marketer'
    ? '/LeadsForMarketer'
    : userlogin?.data?.role === 'Accountant'
    ? '/LeadsForAccountant'
    : '/Dashboard';

  /* checkbox */
  const isAllSelected  = pageItems.length > 0 && pageItems.every(i => selectedItems.includes(i._id));
  const isSomeSelected = selectedItems.length > 0 && !isAllSelected;
  const handleSelectAll = e => {
    if (e.target.checked) setSelectedItems(pageItems.map(i => i._id));
    else setSelectedItems([]);
  };
  const handleSelectOne = id =>
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  /* restore single */
  const openConfirm = item => setConfirmModal({ open:true, id:item._id, name:item.name });

  /* restore confirm */
  const handleRestoreConfirm = async () => {
    setRestoreLoading(true);
    await dispatch(UpdateOneCityData(confirmModal.id, { active: true }));
    setRestoreLoading(false);
    setConfirmModal({ open:false, id:null, name:'' });
    Notify('success', 'City restored successfully', 'success');
    getallcities();
  };

  /* bulk restore */
  const handleBulkRestore = async () => {
    setRestoreLoading(true);
    for (const id of selectedItems) {
      await dispatch(UpdateOneCityData(id, { active: true }));
    }
    setRestoreLoading(false);
    Notify('success', `${selectedItems.length} city(ies) restored successfully`, 'success');
    setSelectedItems([]);
    getallcities();
  };

  /* pagination numbers */
  const buildPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length:totalPages }, (_, i) => i + 1);
    const pages = [];
    if (page <= 4)                   pages.push(1,2,3,4,5,'...',totalPages);
    else if (page >= totalPages - 3) pages.push(1,'...',totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages);
    else                             pages.push(1,'...',page-1,page,page+1,'...',totalPages);
    return pages;
  };

  /* ── inline CSS ── */
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

    @keyframes pm-fade  { from{opacity:0} to{opacity:1} }
    @keyframes pm-slide { from{opacity:0;transform:translateY(24px) scale(0.96)} to{opacity:1;transform:none} }
    @keyframes pm-spin  { to{transform:rotate(360deg)} }

    .ci-shell * { box-sizing:border-box; font-family:'DM Sans',sans-serif; }

    /* ── Stats ── */
    .ci-stats { display:flex; gap:16px; padding:20px 20px 0; flex-wrap:wrap; }
    .ci-stat-card { flex:1; min-width:190px; border-radius:16px; padding:22px 24px; display:flex; justify-content:space-between; align-items:flex-start; border:1px solid #e8eaf0; }
    .ci-stat-card.light { background:#fff; box-shadow:0 1px 4px rgba(16,24,40,.06); }
    .ci-stat-card.dark-card {
      background:linear-gradient(135deg,${ACCENT_DARK},${ACCENT});
      border-color:transparent; box-shadow:0 4px 20px rgba(13,71,161,0.40);
    }
    .ci-stat-label       { font-size:11px; font-weight:600; letter-spacing:.08em; color:#7B829A; text-transform:uppercase; margin-bottom:8px; }
    .ci-stat-label.white { color:rgba(255,255,255,.75); }
    .ci-stat-value       { font-size:32px; font-weight:800; color:#1C2340; line-height:1; letter-spacing:-1px; }
    .ci-stat-value.large { font-size:36px; }
    .ci-stat-value.white { color:#fff; }
    .ci-stat-badge       { display:inline-flex; align-items:center; gap:4px; margin-top:10px; font-size:12px; font-weight:600; padding:3px 10px; border-radius:999px; }
    .ci-stat-badge.blue  { background:${ACCENT_SOFT}; color:${ACCENT}; }
    .ci-badge-pill       { background:rgba(255,255,255,.22); color:#fff; font-size:10px; font-weight:700; padding:3px 10px; border-radius:999px; letter-spacing:.06em; }
    .ci-stat-icon        { width:52px; height:52px; border-radius:14px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .ci-stat-icon.light  { background:${ACCENT_SOFT}; color:${ACCENT}; }
    .ci-stat-icon.dark   { background:rgba(255,255,255,.18); color:#fff; }

    .dark .ci-stat-card.light { background:#1a1a2e; border-color:#2a2a45; }
    .dark .ci-stat-label      { color:#6070a0; }
    .dark .ci-stat-value      { color:#e0e0ff; }
    .dark .ci-stat-icon.light { background:rgba(13,71,161,0.25); color:#90b4ff; }

    /* ── Table Section ── */
    .ci-table-section { margin:20px; border-radius:16px; overflow:hidden; border:1px solid #e8eaf0; background:#fff; display:flex; flex-direction:column; }
    .dark .ci-table-section { background:#1a1a2e; border-color:#2a2a45; }

    .ci-table-header { display:flex; align-items:center; justify-content:space-between; padding:20px 24px; border-bottom:1px solid #e8eaf0; flex-wrap:wrap; gap:12px; }
    .dark .ci-table-header { border-color:#2a2a45; }
    .ci-title-label { font-size:10px; font-weight:700; letter-spacing:.12em; color:#7B829A; text-transform:uppercase; margin-bottom:2px; }
    .ci-title       { font-size:18px; font-weight:800; color:#1C2340; letter-spacing:-.3px; }
    .dark .ci-title { color:#e0e0ff; }
    .ci-actions     { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

    /* search */
    .ci-search { display:flex; align-items:center; gap:8px; padding:9px 14px; border:1.5px solid #e0e2ef; border-radius:10px; background:#f7f8fc; color:#7B829A; min-width:250px; }
    .dark .ci-search { background:#252540; border-color:#3a3a5a; color:#7070a0; }
    .ci-search input { border:none; background:none; outline:none; font-size:13px; color:#1C2340; font-family:'DM Sans',sans-serif; width:100%; }
    .dark .ci-search input { color:#e0e0ff; }
    .ci-search input::placeholder { color:#9ca3af; }

    /* buttons */
    .ci-btn-primary {
      display:flex; align-items:center; gap:6px; padding:9px 16px;
      background:${ACCENT}; border:none; border-radius:8px; cursor:pointer;
      font-size:13px; font-weight:600; color:#fff; font-family:'DM Sans',sans-serif;
      transition:background 0.15s; white-space:nowrap;
    }
    .ci-btn-primary:hover { background:${ACCENT_DARK}; }

    .ci-btn-restore {
      display:flex; align-items:center; gap:6px; padding:9px 14px;
      background:#16a34a; border:none; border-radius:8px; cursor:pointer;
      font-size:13px; font-weight:600; color:#fff; font-family:'DM Sans',sans-serif;
      white-space:nowrap; transition:background 0.15s;
    }
    .ci-btn-restore:hover    { background:#15803d; }
    .ci-btn-restore:disabled { opacity:0.6; cursor:not-allowed; }

    /* ── Table ── */
    .ci-table-wrap { flex:1; overflow:auto; }
    .ci-table { width:100%; border-collapse:collapse; font-size:13px; }
    .ci-table th {
      padding:12px 16px; text-align:left; font-size:10.5px; font-weight:700;
      letter-spacing:.08em; text-transform:uppercase; color:#7B829A;
      border-bottom:2px solid #e8eaf0; white-space:nowrap;
      position:sticky; top:0; z-index:2; background:#fff;
    }
    .dark .ci-table th { border-color:#2a2a45; background:#1a1a2e; }
    .ci-table td { padding:14px 16px; border-bottom:1px solid #f0f2f8; vertical-align:middle; }
    .dark .ci-table td { border-color:#252540; }

    .ci-row { transition:background 0.12s; }
    .ci-row:hover { background:#f5f7ff !important; }
    .dark .ci-row:hover { background:#1e1e3a !important; }
    .ci-row:last-child td { border-bottom:none; }

    /* cells */
    .ci-name-cell { display:flex; align-items:center; gap:10px; }
    .ci-thumb {
      width:38px; height:38px; border-radius:10px; flex-shrink:0;
      background:linear-gradient(135deg,${ACCENT_DARK},${ACCENT});
      display:flex; align-items:center; justify-content:center;
      font-size:13px; font-weight:700; color:#fff; letter-spacing:0.5px;
    }
    .ci-name    { font-size:14px; font-weight:600; color:#1C2340; }
    .dark .ci-name { color:#e0e0ff; }
    .ci-id-copy { font-size:11px; color:${ACCENT}; cursor:pointer; }
    .dark .ci-id-copy { color:#90b4ff; }
    .ci-muted { color:#6b7280; font-size:13px; }
    .dark .ci-muted { color:#7070a0; }

    /* action btns */
    .ci-btn-actions { display:flex; align-items:center; gap:6px; }
    .ci-action-btn {
      width:32px; height:32px; border-radius:8px; border:1.5px solid #e8eaf0;
      background:#f7f8fc; cursor:pointer; display:flex; align-items:center; justify-content:center;
      color:#7B829A; transition:all 0.15s;
    }
    .dark .ci-action-btn { background:#252540; border-color:#3a3a5a; color:#9099c0; }
    .ci-action-btn.copy:hover    { background:${ACCENT_SOFT}; border-color:${ACCENT}; color:${ACCENT}; }
    .ci-action-btn.restore:hover { background:rgba(22,163,74,0.1); border-color:#16a34a; color:#16a34a; }

    .ci-empty { text-align:center; padding:60px 20px !important; color:#7B829A; }
    .ci-empty p { margin:8px 0 0; font-size:14px; }
    .ci-spinner { width:32px; height:32px; border:3px solid #e8eaf0; border-top-color:${ACCENT}; border-radius:50%; animation:pm-spin 0.7s linear infinite; margin:0 auto 10px; }
    .ci-checkbox { width:16px; height:16px; cursor:pointer; accent-color:${ACCENT}; }

    /* ── Pagination ── */
    .ci-pagination { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; flex-wrap:wrap; gap:10px; }
    .ci-pag-info { display:flex; align-items:center; gap:10px; font-size:13px; color:#7B829A; flex-wrap:wrap; }
    .dark .ci-pag-info { color:#6070a0; }
    .ci-pag-info strong { color:#1C2340; }
    .dark .ci-pag-info strong { color:#e0e0ff; }
    .ci-pag-select { border:1.5px solid #e0e2ef; border-radius:8px; padding:5px 8px; font-size:13px; font-family:'DM Sans',sans-serif; outline:none; background:#fff; color:#1C2340; cursor:pointer; }
    .dark .ci-pag-select { background:#252540; border-color:#3a3a5a; color:#e0e0ff; }
    .ci-pag-btns { display:flex; align-items:center; gap:4px; }
    .ci-pag-btn { min-width:34px; height:34px; border-radius:8px; border:1.5px solid #e0e2ef; background:#fff; color:#7B829A; font-size:13px; font-family:'DM Sans',sans-serif; cursor:pointer; padding:0 8px; font-weight:500; transition:all 0.15s; }
    .ci-pag-btn:hover:not(:disabled) { background:${ACCENT_SOFT}; border-color:${ACCENT}; color:${ACCENT}; }
    .ci-pag-btn.active { background:${ACCENT}; border-color:${ACCENT}; color:#fff; font-weight:700; }
    .ci-pag-btn:disabled { opacity:0.35; cursor:not-allowed; }
    .dark .ci-pag-btn { background:#252540; border-color:#3a3a5a; color:#7070a0; }
    .dark .ci-pag-btn:hover:not(:disabled) { background:rgba(13,71,161,0.25); border-color:#90b4ff; color:#90b4ff; }
    .dark .ci-pag-btn.active { background:${ACCENT}; border-color:${ACCENT}; color:#fff; }
    .ci-pag-ellipsis { padding:0 4px; color:#7B829A; font-size:14px; }

    /* ── Footer ── */
    .ci-footer { padding:16px 20px; font-size:12px; color:#9ca3af; border-top:1px solid #f0f2f8; }
    .dark .ci-footer { border-color:#2a2a45; }
  `;

  /* ── render ── */
  return (
    <>
      <style>{css}</style>

      <div
        className={`ci-shell ${isDark ? 'dark' : ''}`}
        style={{
          position:'fixed', top:'50px', left:sidebarWidth, right:0, bottom:0,
          transition:'left 0.25s cubic-bezier(0.4,0,0.2,1)',
          overflowY:'auto', overflowX:'hidden',
          display:'flex', flexDirection:'column',
          background: isDark ? '#111122' : '#f7f8fc',
        }}
      >

        {/* ─── Breadcrumb ─── */}
        <section style={{ padding:'16px 20px 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px' }}>
            <Link to={homeRoute} style={{ textDecoration:'none', color:isDark?'#9ca3af':'#6b7280', fontWeight:500 }}>Home</Link>
            <span style={{ color:isDark?'#4b5563':'#d1d5db' }}>/</span>
            <Link to="/AllCitiesShow" style={{ textDecoration:'none', color:isDark?'#9ca3af':'#6b7280', fontWeight:500 }}>Cities</Link>
            <span style={{ color:isDark?'#4b5563':'#d1d5db' }}>/</span>
            <span style={{ color:isDark?'#e5e7eb':'#111827', fontWeight:600 }}>Trash</span>
          </div>
        </section>

        {/* ─── Stats ─── */}
        <section className="ci-stats" style={{ flexShrink:0 }}>
          {/* Total */}
          <div className="ci-stat-card light">
            <div>
              <div className="ci-stat-label">TOTAL IN TRASH</div>
              <div className="ci-stat-value">{allItems.length}</div>
              <div className="ci-stat-badge blue">↩ Restorable</div>
            </div>
            <div className="ci-stat-icon light"><IconCity /></div>
          </div>

          {/* This month */}
          <div className="ci-stat-card light">
            <div>
              <div className="ci-stat-label">DELETED THIS MONTH</div>
              <div className="ci-stat-value large">{thisMonth}</div>
              <div className="ci-stat-badge blue">Last 30 days</div>
            </div>
            <div className="ci-stat-icon light"><IconCalendar /></div>
          </div>

          {/* Filtered – dark card */}
          <div className="ci-stat-card dark-card">
            <div>
              <div className="ci-stat-label white">FILTERED RESULTS</div>
              <div className="ci-stat-value white">{filtered.length}</div>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'12px' }}>
                <span className="ci-badge-pill">SHOWING NOW</span>
                <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'11px' }}>of {allItems.length} total</span>
              </div>
            </div>
            <div className="ci-stat-icon dark"><IconTrend /></div>
          </div>
        </section>

        {/* ─── Table Section ─── */}
        <section
          className="ci-table-section"
          style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minHeight:0 }}
        >
          {/* header */}
          <div className="ci-table-header" style={{ flexShrink:0 }}>
            <div>
              <div className="ci-title-label">MANAGEMENT</div>
              <div className="ci-title">CITIES TRASH</div>
            </div>
            <div className="ci-actions">
              {/* Search */}
              <div className="ci-search">
                <IconSearch />
                <input
                  placeholder="Search by city name…"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                />
              </div>

              {/* Bulk Restore */}
              {selectedItems.length > 0 && (
                <button className="ci-btn-restore" onClick={handleBulkRestore} disabled={restoreLoading}>
                  {restoreLoading
                    ? <><div style={{ width:14,height:14,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'pm-spin 0.7s linear infinite' }}/>Restoring…</>
                    : <><IconRestore /> Restore ({selectedItems.length})</>}
                </button>
              )}

              {/* Back */}
              <Link to="/AllCitiesShow" style={{ textDecoration:'none' }}>
                <button className="ci-btn-primary">← All Cities</button>
              </Link>
            </div>
          </div>

          {/* Table */}
          <div className="ci-table-wrap" style={{ flex:1, overflowY:'auto', overflowX:'auto' }}>
            <table className="ci-table">
              <thead>
                <tr>
                  <th style={{ width:46 }}>
                    <input
                      type="checkbox" className="ci-checkbox"
                      checked={isAllSelected}
                      ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {['CITY NAME', 'CREATED DATE', 'ACTIONS'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="ci-empty">
                      <div className="ci-spinner"/>
                      <span>Loading cities…</span>
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ci-empty">
                      <IconSearch />
                      <p>No deleted cities found</p>
                    </td>
                  </tr>
                ) : pageItems.map(city => (
                  <tr
                    key={city._id}
                    className="ci-row"
                    style={{ background: selectedItems.includes(city._id) ? (isDark?'rgba(13,71,161,0.12)':'#eef3fb') : undefined }}
                  >
                    {/* Checkbox */}
                    <td>
                      <input
                        type="checkbox" className="ci-checkbox"
                        checked={selectedItems.includes(city._id)}
                        onChange={() => handleSelectOne(city._id)}
                      />
                    </td>

                    {/* City Name */}
                    <td>
                      <div className="ci-name-cell">
                        <div className="ci-thumb">
                          {(city.name || 'CT').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="ci-name">{city.name || '—'}</div>
                          <CopyToClipboard text={city._id} onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}>
                            <span className="ci-id-copy" title="Copy ID">#{city._id?.slice(-6)}</span>
                          </CopyToClipboard>
                        </div>
                      </div>
                    </td>

                    {/* Created Date */}
                    <td>
                      <div style={{ fontSize:13 }}>
                        <div style={{ fontWeight:600, color:isDark?'#c0c0e0':'#374151' }}>
                          {city.createdAt ? moment(city.createdAt).format('MMM DD, YYYY') : '—'}
                        </div>
                        <div style={{ fontSize:11, color:isDark?'#6070a0':'#9ca3af', marginTop:2 }}>
                          {city.createdAt ? moment(city.createdAt).format('h:mm A') : ''}
                        </div>
                      </div>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="ci-btn-actions">
                        <CopyToClipboard text={city._id} onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}>
                          <button className="ci-action-btn copy" title="Copy ID"><IconCopy /></button>
                        </CopyToClipboard>
                        <button
                          className="ci-action-btn restore"
                          title="Restore City"
                          onClick={() => openConfirm(city)}
                        >
                          <IconRestore />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination sticky ── */}
          {filtered.length > 0 && (
            <div
              className="ci-pagination"
              style={{ position:'sticky', bottom:0, zIndex:5, flexShrink:0, backgroundColor:isDark?'#1a1a2e':'#fff', borderTop:`1px solid ${isDark?'#2a2a45':'#e8eaf0'}`, boxShadow:'0 -2px 12px rgba(0,0,0,0.06)' }}
            >
              <div className="ci-pag-info">
                <span>Items per page:</span>
                <select className="ci-pag-select" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setPage(1); }}>
                  {[5,10,25,50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span>Showing <strong>{(page-1)*itemsPerPage+1}–{Math.min(page*itemsPerPage,filtered.length)}</strong> of <strong>{filtered.length}</strong> cities</span>
              </div>
              <div className="ci-pag-btns">
                <button className="ci-pag-btn" onClick={() => setPage(1)} disabled={page===1}>⟨|</button>
                <button className="ci-pag-btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}>‹</button>
                {buildPageNumbers().map((n,i) =>
                  n === '...'
                    ? <span key={`e-${i}`} className="ci-pag-ellipsis">…</span>
                    : <button key={n} className={`ci-pag-btn ${page===n?'active':''}`} onClick={() => setPage(n)}>{n}</button>
                )}
                <button className="ci-pag-btn" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}>›</button>
                <button className="ci-pag-btn" onClick={() => setPage(totalPages)} disabled={page===totalPages}>|⟩</button>
              </div>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="ci-footer" style={{ flexShrink:0 }}>
          <span>© 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.</span>
        </footer>
      </div>

      {/* ─── Restore Confirm Modal ─── */}
      {confirmModal.open && (
        <PortalModal
          isDark={isDark}
          title="Restore City?"
          subtitle={`"${confirmModal.name}" will be restored and become active again.`}
          onClose={() => setConfirmModal({ open:false, id:null, name:'' })}
          onConfirm={handleRestoreConfirm}
          confirmLabel={<><IconRestore /> Restore</>}
          confirmColor="#16a34a"
          loading={restoreLoading}
        />
      )}

      {/* ─── Copy Snackbar ─── */}
      <Snackbar open={copyOpen} autoHideDuration={2000} anchorOrigin={{ vertical:'bottom', horizontal:'center' }}>
        <Alert severity="success" sx={{ width:'100%' }}>Copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};
